import { makeStyles } from '@material-ui/core';

export const useModalStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
  },
  content: {
    padding: theme.spacing(3, 2),
    minHeight: 400,
    maxHeight: '75vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: 4,
      '&:hover': {
        background: '#a8a8a8',
      },
    },
  },
  titleChildrenStyle: {
    width: 'auto',
    minWidth: 200,
    flexShrink: 0,
  },
  actions: {
    padding: theme.spacing(3, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
  cancelButton: {
    color: theme.palette.text.secondary,
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  stepContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const useStep1Styles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(5),
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(2),
  },
  formSection: {
    padding: theme.spacing(2),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#666',
    marginBottom: theme.spacing(3),
  },
  emailInputContainer: {
    marginBottom: theme.spacing(2),
  },
  emailLabel: {
    display: 'block',
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: '#333',
    fontSize: '0.95rem',
  },
  emailInput: {
    width: '100%',
    padding: theme.spacing(1.5, 2),
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 16,
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
    },
    '&.error': {
      borderColor: theme.palette.error.main,
    },
  },
  checkButton: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  illustration: {
    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    borderRadius: 12,
    padding: theme.spacing(5),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  illustrationIcon: {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  illustrationText: {
    color: '#666',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    '& strong': {
      color: '#333',
      display: 'block',
      marginBottom: theme.spacing(0.5),
    },
  },
  statusMessage: {
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  statusChecking: {
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    color: '#075985',
  },
  statusExists: {
    background: '#fef3c7',
    border: '1px solid #fde68a',
    color: '#92400e',
  },
  statusAvailable: {
    background: '#d1fae5',
    border: '1px solid #a7f3d0',
    color: '#065f46',
  },
  statusError: {
    background: '#fee2e2',
    border: '1px solid #fecaca',
    color: '#991b1b',
  },
  statusIcon: {
    width: 20,
    height: 20,
  },
  editButton: {
    marginLeft: 'auto',
    background: 'white',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(1, 2),
    borderRadius: 6,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: theme.palette.primary.main,
      color: 'white',
    },
  },
}));

export const useStep2Styles = makeStyles((theme) => ({
  container: {
    maxWidth: 800, // Increased width for horizontal layout
    margin: '0 auto',
    padding: theme.spacing(3),
    width: '100%',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#666',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  section: {
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    alignItems: 'start', // Center align email and phone fields

    // Responsive design
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
      alignItems: 'stretch',
    },

    '&:last-child': {
      marginBottom: 0,
    },
  },
  halfWidth: {
    flex: 1,

    // Ensure consistent spacing
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
  emailFieldContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end', // Align to bottom like FormikTextField
    marginTop: 8,
  },
  emailReadOnly: {
    background: '#f5f5f5',
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    border: '1px solid #e0e0e0',
    color: '#666',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 40, // Fixed height as requested
    minHeight: 40,
    maxHeight: 40,

    // Add subtle styling to match other fields
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  emailText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  verifiedIcon: {
    fontSize: 16,
    color: '#4caf50', // Green color for verification
    flexShrink: 0,
  },
  lockIcon: {
    fontSize: 16,
    color: '#999',
    flexShrink: 0,
  },
}));
